import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as api from "../../redux/api";
import { debounce } from "lodash";
import OrganizationModal from "../../components/modals/OrganizationModal";

const ParticularUserData = ({ userDetail }) => {
  const [open, setOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userType, setUserType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getUserData = async () => {
    setIsLoading(true);
    try {
      const response = await api.particularUserData(
        userDetail?.id,
        userDetail?.type
      );
      const { data, status } = response;

      if (status >= 200 && status < 300) {
        setIsLoading(false);
        if (data.data && data.data.organizationRole?.length > 0) {
          setUserData(data.data.organizationRole[0]);
          setUserType(data?.data?.type);
        } else {
          setUserData(null);
          toast.error("No organization role data found");
        }
      } else {
        setIsLoading(false);
        toast.error(data.message);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Error fetching data");
      console.error(error);
    }
  };

  const debouncedFetchUsers = useCallback(
    debounce(() => getUserData(), 500),
    []
  );

  useEffect(() => {
    debouncedFetchUsers();
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [debouncedFetchUsers]);

  const renderImages = (array) => {
    if (!array || array.length === 0) return <span>None</span>;
    return (
      <div className="flex flex-wrap gap-2">
        {array.map((src, index) => (
          <img
            key={index}
            src={`https://ventursa-api.codesfortomorrow.com/storage/${src}`}
            alt={`certificate-${index}`}
            className="w-20 h-20 object-cover rounded"
          />
        ))}
      </div>
    );
  };

  const renderUserDetails = () => {
    if (!userData) return null;

    switch (userType) {
      case "Mine":
        return (
          <>
            <div className="gap-10">
              Mine Production Capacity:
              {userData.mineProductionCapacity || "Not specified"}
            </div>
            <div>
              Mine Efficiency Metrics:
              {userData.mineEfficiencyMetrics || "Not specified"}
            </div>
            <div>
              Mine Certification: {renderImages(userData.mineCertification)}
            </div>
            <div>
              Mine Operational License:
              {renderImages(userData.mineOperationalLicense)}
            </div>
            <div>
              Mine Environmental Compliance:
              {renderImages(userData.mineEnvironmentalCompliance)}
            </div>
          </>
        );
      case "LogisticsProvider":
        return (
          <>
            <div>
              Primary Transport Methods:
              {userData.logisticsPrimaryTransportMethods}
            </div>
            <div>
              Third Party Carriers:
              {userData.logisticsThirdPartyCarriers}
            </div>
            <div>
              Areas of Operation:
              {userData.logisticsAreasOfOperation}
            </div>
            <div>
              Proof of Insurance Policy:
              {renderImages(userData.logisticsProofOfInsurancePolicy)}
            </div>
            <div>
              Vehicle Fleet Registration:
              {renderImages(userData.logisticsVehicleFleetRegistration)}
            </div>
          </>
        );
      case "Refinery":
        return (
          <>
            <div>Method Used: {userData.refineryMethodUsed}</div>
            <div>
              Sustainability Practices:
              {userData.refinerySustainabilityPractices}
            </div>
            <div>
              Refinery Certification:
              {renderImages(userData.refineryCertification)}
            </div>
            <div>
              ISO Certification:
              {renderImages(userData.refineryIsoCertification)}
            </div>
            <div>
              Operational License:
              {renderImages(userData.refineryOperationalLicense)}
            </div>
          </>
        );
      case "Vault":
        return (
          <>
            <div>
              Facility Description:
              {userData.vaultFacilityDescription || "Not specified"}
            </div>
            <div>
              Storage Capacity:
              {userData.vaultStorageCapacity || "Not specified"}
            </div>
            <div>
              Security Measures:
              {userData.vaultSecurityMeasures || "No Security"}
            </div>
            <div>
              Insurance Type:
              {userData.vaultInsuranceType || "Not specified"}
            </div>
            <div className="space-y">
              Liability Insurance Document:
              {renderImages(userData.vaultLiabilityInsuranceDocument)}
            </div>
            <div className="space-y-3">
              Security Audit Report:
              {renderImages(userData.vaultSecurityAuditReport)}
            </div>
          </>
        );
      default:
        return <p>No organization role data available</p>;
    }
  };

  return (
    <>
      <div className="w-full mt-10 bg-white p-6 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {isLoading ? <p>Loading...</p> : renderUserDetails()}
        </div>
      </div>
      <OrganizationModal
        open={open}
        handleClose={() => setOpen(false)}
        userData={userData}
      />
    </>
  );
};

export default ParticularUserData;
